import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Check, 
  X, 
  Clock, 
  TrendingUp,
  Users,
  DollarSign,
  Zap,
  Shield,
  ChevronDown,
  ChevronUp,
  Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";

// Sticky CTA Bar Component
const StickyCTA = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-[#0B1120] to-[#1a1a2e] border-t border-white/10 py-4 px-4 z-50 backdrop-blur-lg"
    >
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        <p className="text-white/80 text-sm hidden sm:block">Ready to start building recurring income?</p>
        <Button
          onClick={() => window.open('https://checkout.partnerwithaspirepartners.com/', '_blank')}
          className="bg-gradient-to-r from-[#0099CC] to-[#00d4ff] hover:from-[#007EA8] hover:to-[#00b8e6] text-white rounded-full py-3 px-8 font-semibold shadow-lg shadow-cyan-500/30 w-full sm:w-auto"
        >
          Secure Your Spot
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </motion.div>
  );
};

// FAQ Item Component
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-white/10">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center justify-between text-left"
      >
        <span className="text-white font-medium">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-[#0099CC]" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[#0099CC]" />
        )}
      </button>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          className="pb-4"
        >
          <p className="text-white/70">{answer}</p>
        </motion.div>
      )}
    </div>
  );
};

// Main CTA Button Component
const CTAButton = ({ className = "" }) => (
  <Button
    onClick={() => window.open('https://checkout.partnerwithaspirepartners.com/', '_blank')}
    className={`bg-gradient-to-r from-[#0099CC] to-[#00d4ff] hover:from-[#007EA8] hover:to-[#00b8e6] text-white rounded-full py-6 px-10 text-lg font-semibold shadow-lg shadow-cyan-500/30 hover:scale-105 transition-all ${className}`}
  >
    Secure Your Spot Now
    <ArrowRight className="ml-2 w-5 h-5" />
  </Button>
);

// Risk-Free Guarantee Badge Component
const GuaranteeBadge = ({ variant = "dark" }) => (
  <div className={`rounded-xl p-4 flex items-center justify-center gap-3 ${
    variant === "dark" 
      ? "bg-emerald-500/10 border border-emerald-500/30" 
      : "bg-emerald-50 border-2 border-emerald-200"
  }`}>
    <Shield className={`w-8 h-8 ${variant === "dark" ? "text-emerald-400" : "text-emerald-500"}`} />
    <div className="text-center">
      <p className={`font-bold ${variant === "dark" ? "text-emerald-400" : "text-emerald-600"}`}>100% RISK-FREE</p>
      <p className={`text-sm ${variant === "dark" ? "text-emerald-300" : "text-emerald-700"}`}>30-Day Money-Back Guarantee</p>
    </div>
  </div>
);

const ComebackPage = () => {
  return (
    <div className="min-h-screen bg-[#0B1120]">
      <StickyCTA />
      
      {/* SECTION 1: HERO */}
      <section className="min-h-[90vh] flex items-center relative overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="https://customer-assets.emergentagent.com/job_intro-income/artifacts/obl10dyz_5020872_4k_4k_Footage_3840x2160.mp4" type="video/mp4" />
          </video>
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0 bg-[#0B1120]/80" />
        </div>
        
        {/* Gradient accents */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#0099CC]/10 rounded-full blur-[150px] z-[1]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[120px] z-[1]" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
              One Connection Could Change Your{" "}
              <span className="bg-gradient-to-r from-[#0099CC] to-[#00d4ff] bg-clip-text text-transparent">
                Income Forever
              </span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed">
              You already showed interest. This is where you decide if you stay where you are... 
              or finally build something that pays you monthly.
            </p>
            
            <CTAButton />
            
            <div className="mt-6 max-w-sm mx-auto">
              <GuaranteeBadge variant="dark" />
            </div>
            
            <p className="text-white/50 text-sm mt-4">
              Limited partner openings • Takes 2 minutes to start
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2: PATTERN INTERRUPT */}
      <section className="py-20 px-4 relative">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-8">
              Let's Be Honest For a Second...
            </h2>
            
            <p className="text-xl text-white/70 mb-8">
              You didn't ignore this opportunity because it doesn't make sense.
            </p>
            
            <p className="text-lg text-white/60 mb-4">You paused because:</p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-10">
              {["You're busy", "You're unsure", "You're used to overthinking decisions"].map((reason) => (
                <span key={reason} className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-white/80">
                  {reason}
                </span>
              ))}
            </div>
            
            <p className="text-lg text-white/70 mb-6">But here's the reality:</p>
            
            <p className="text-2xl text-[#0099CC] font-semibold mb-10">
              The biggest financial opportunities are usually the ones people hesitate on
            </p>
            
            <div className="bg-gradient-to-r from-purple-500/20 to-[#0099CC]/20 rounded-2xl p-6 border border-white/10">
              <p className="text-xl text-white font-medium">
                And while you're thinking about it... <span className="text-[#0099CC]">someone else is taking action.</span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 3: PAIN + REALITY */}
      <section className="py-20 px-4 bg-gradient-to-b from-transparent via-red-500/5 to-transparent">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              The System Most People Rely On Is{" "}
              <span className="text-red-400">Breaking</span>
            </h2>
          </motion.div>
          
          <div className="grid sm:grid-cols-2 gap-6 mb-12">
            {[
              "Job security is disappearing",
              "AI is replacing roles faster than expected",
              "Income is capped but expenses aren't",
              "Working harder isn't creating freedom"
            ].map((point, idx) => (
              <motion.div
                key={point}
                initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex items-center gap-4 bg-white/5 rounded-xl p-5 border border-white/10"
              >
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                  <X className="w-5 h-5 text-red-400" />
                </div>
                <p className="text-white/80">{point}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center">
            <p className="text-2xl text-white font-semibold">
              If your income depends on your time... <span className="text-[#0099CC]">you don't have control</span>
            </p>
          </div>
        </div>
      </section>

      {/* CTA BREAK */}
      <section className="py-16 px-4 text-center">
        <CTAButton />
      </section>

      {/* SECTION 4: HOW IT WORKS */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              This Is Why <span className="text-[#0099CC]">Aspire Partners</span> Works
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "You Make a Connection",
                description: "Think of a business that might be overspending",
                icon: Users
              },
              {
                step: "2",
                title: "Aspire Handles Everything",
                description: "Audits, closes, manages, implements",
                icon: Zap
              },
              {
                step: "3",
                title: "You Get Paid Monthly",
                description: "Recurring income for the life of the deal",
                icon: DollarSign
              }
            ].map((item, idx) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
                className="relative"
              >
                <div className="bg-gradient-to-b from-white/10 to-white/5 rounded-2xl p-8 border border-white/10 text-center h-full">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#0099CC] to-[#00d4ff] flex items-center justify-center">
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-5xl font-bold text-[#0099CC]/30 mb-2">{item.step}</div>
                  <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-white/60">{item.description}</p>
                </div>
                {idx < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-8 h-8 text-[#0099CC]/50" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5: OBJECTION KILLER */}
      <section className="py-20 px-4 bg-gradient-to-b from-transparent via-[#0099CC]/5 to-transparent">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              What You <span className="text-red-400">DON'T</span> Have to Do
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-12">
            {[
              "No selling",
              "No chasing clients",
              "No employees",
              "No overhead",
              "No experience required"
            ].map((item, idx) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-center"
              >
                <X className="w-6 h-6 text-red-400 mx-auto mb-2" />
                <p className="text-white/80 text-sm">{item}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center space-y-3">
            <p className="text-xl text-white">
              This isn't a business you build from scratch
            </p>
            <p className="text-2xl text-[#0099CC] font-semibold">
              It's a system you plug into
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 6: INCOME VISION */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              This Starts Small... But It{" "}
              <span className="bg-gradient-to-r from-[#0099CC] to-[#00d4ff] bg-clip-text text-transparent">
                Adds Up Fast
              </span>
            </h2>
          </motion.div>
          
          <div className="space-y-6 mb-12">
            {[
              { connections: "3-5 connections", result: "Extra monthly income", level: 1 },
              { connections: "10-15 connections", result: "Serious income stream", level: 2 },
              { connections: "20+ connections", result: "Life-changing recurring income", level: 3 }
            ].map((tier, idx) => (
              <motion.div
                key={tier.connections}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className="relative"
              >
                <div 
                  className={`flex items-center justify-between p-6 rounded-xl border ${
                    tier.level === 3 
                      ? 'bg-gradient-to-r from-[#0099CC]/20 to-purple-500/20 border-[#0099CC]/30' 
                      : 'bg-white/5 border-white/10'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      tier.level === 3 ? 'bg-[#0099CC]' : 'bg-white/10'
                    }`}>
                      <TrendingUp className={`w-6 h-6 ${tier.level === 3 ? 'text-white' : 'text-[#0099CC]'}`} />
                    </div>
                    <span className="text-white font-medium">{tier.connections}</span>
                  </div>
                  <span className={`font-semibold ${tier.level === 3 ? 'text-[#0099CC] text-lg' : 'text-white/70'}`}>
                    {tier.result}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center">
            <p className="text-2xl text-white font-semibold">
              And you already have the network to start
            </p>
          </div>
        </div>
      </section>

      {/* CTA BREAK */}
      <section className="py-16 px-4 text-center">
        <CTAButton />
        <p className="text-white/50 text-sm mt-4">Limited partner openings</p>
      </section>

      {/* SECTION 7: HESITATION BREAKER */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-8">
              The Only Thing Holding You Back Is{" "}
              <span className="text-[#0099CC]">Waiting</span>
            </h2>
            
            <p className="text-xl text-white/70 mb-8">
              You don't need more information.
            </p>
            
            <p className="text-lg text-white/60 mb-8">You need to decide:</p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 flex-1">
                <p className="text-white/50">Stay where you are</p>
              </div>
              <div className="flex items-center justify-center text-white/30">or</div>
              <div className="bg-gradient-to-r from-[#0099CC]/20 to-purple-500/20 border border-[#0099CC]/30 rounded-xl p-6 flex-1">
                <p className="text-[#0099CC] font-semibold">Take a step toward something better</p>
              </div>
            </div>
            
            <p className="text-xl text-white/70">
              Waiting feels safe... <span className="text-white font-semibold">but it keeps everything the same</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 8: SCARCITY */}
      <section className="py-20 px-4 bg-gradient-to-b from-transparent via-orange-500/5 to-transparent">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Clock className="w-16 h-16 text-orange-400 mx-auto mb-6" />
            
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-8">
              This Isn't Open Forever
            </h2>
            
            <p className="text-lg text-white/70 mb-8">
              Aspire Partners limits partner onboarding to protect opportunity quality.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-10">
              {["Not everyone gets in", "Spots do fill", "And once they do... you're waiting"].map((point) => (
                <span key={point} className="px-4 py-2 bg-orange-500/10 border border-orange-500/20 rounded-full text-orange-300 text-sm">
                  {point}
                </span>
              ))}
            </div>
            
            <p className="text-xl text-white font-semibold">
              The only difference between people earning and people watching... <span className="text-[#0099CC]">is timing</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 9: REFRAME */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              You're Already Doing This...{" "}
              <span className="text-[#0099CC]">Just Not Getting Paid</span>
            </h2>
          </motion.div>
          
          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            {[
              "You recommend restaurants",
              "You refer services",
              "You connect people"
            ].map((item, idx) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-xl p-6 text-center"
              >
                <Check className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                <p className="text-white/80">{item}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center">
            <p className="text-2xl text-white font-semibold">
              Now imagine getting paid every time those connections turn into{" "}
              <span className="text-[#0099CC]">savings for a business</span>
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 10: FUTURE VISION */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Imagine 12 Months From Now
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Nothing Changes */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/5 border border-white/10 rounded-2xl p-8"
            >
              <h3 className="text-xl font-bold text-white/50 mb-6 text-center">If Nothing Changes</h3>
              <div className="space-y-4">
                {["Same income", "Same stress", "Same limitations"].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <X className="w-5 h-5 text-red-400" />
                    <span className="text-white/60">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            {/* If You Start */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-[#0099CC]/20 to-purple-500/20 border border-[#0099CC]/30 rounded-2xl p-8"
            >
              <h3 className="text-xl font-bold text-[#0099CC] mb-6 text-center">If You Start Now</h3>
              <div className="space-y-4">
                {[
                  "Growing monthly income",
                  "More control over your time",
                  "Less dependence on a job"
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-emerald-400" />
                    <span className="text-white">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 11: FINAL CLOSE */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Sparkles className="w-16 h-16 text-[#0099CC] mx-auto mb-6" />
            
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-8">
              You Already Took the First Step
            </h2>
            
            <p className="text-xl text-white/70 mb-4">
              You showed interest for a reason.
            </p>
            
            <p className="text-lg text-white/60 mb-8">
              Don't let this become another "what if."
            </p>
            
            <p className="text-2xl text-[#0099CC] font-bold mb-10">
              Finish what you started.
            </p>
            
            <CTAButton className="text-xl py-7 px-12" />
            
            <p className="text-white/50 text-sm mt-4">
              Takes 2 minutes • Limited availability
            </p>
          </motion.div>
        </div>
      </section>

      {/* MINI FAQ */}
      <section className="py-20 px-4 bg-white/5">
        <div className="max-w-3xl mx-auto">
          <h3 className="text-2xl font-bold text-white mb-8 text-center">Quick Answers</h3>
          
          <div className="space-y-2">
            <FAQItem 
              question="Do I have to sell anything?" 
              answer="No. Your only role is making introductions. Aspire Partners handles all the selling, negotiating, closing, and implementation." 
            />
            <FAQItem 
              question="How do I get paid?" 
              answer="Monthly recurring payments. When the businesses you introduce engage with Aspire's services, you earn ongoing income for the life of the deal." 
            />
            <FAQItem 
              question="Is there risk?" 
              answer="No overhead, no inventory, no employees. You're simply leveraging your network." 
            />
            <FAQItem 
              question="How fast can I start?" 
              answer="Immediately. Once you join, you can start making introductions right away." 
            />
          </div>
        </div>
      </section>

      {/* NETWORK VALUE TEASER */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#0099CC]/10 to-purple-500/10 border border-[#0099CC]/20 rounded-3xl p-10"
          >
            <DollarSign className="w-12 h-12 text-[#0099CC] mx-auto mb-4" />
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Your Network Might Be Worth More Than Your Salary
            </h3>
            <p className="text-white/60 mb-6">
              Find out what your connections could be earning you.
            </p>
            <Link to="/#calculator">
              <Button variant="outline" className="border-[#0099CC] text-[#0099CC] hover:bg-[#0099CC]/10 rounded-full py-5 px-8">
                Find Out What Your Network Could Earn
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 px-4 bg-gradient-to-t from-[#0099CC]/10 to-transparent">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8">
            Ready to Start Building Recurring Income?
          </h2>
          <CTAButton className="text-xl py-7 px-12" />
          <p className="text-white/50 text-sm mt-4">
            Join Aspire Partners Pro • Limited availability
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Aspire Partners LLC. All rights reserved.
          </p>
          <p className="text-white/30 text-xs mt-4 max-w-2xl mx-auto">
            Income potential is not guaranteed and will vary based on individual effort, experience, network, and market conditions. 
            Results shown are for illustrative purposes only.
          </p>
        </div>
      </footer>
      
      {/* Bottom padding for sticky CTA */}
      <div className="h-20" />
    </div>
  );
};

export default ComebackPage;
